import { useAuth0 } from "@auth0/auth0-react";
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { InboxChangedMessage } from "entities/ISocketArgs";
import AgentDomain from "entities/domain/agents/agent-domain";
import InboxDomain from "entities/domain/inbox";
import { conversationTemplatesMerchantQueryKey } from "hooks/queries/use-conversations";
import { unreadCountsQueryKey } from "hooks/queries/use-current-agent";
import { useMerchant } from "hooks/queries/use-settings-merchant";
import { useWebSocket } from "hooks/use-socket";
import { useEffect } from "react";
import InboxService from "services/inbox";
import { roleInboxAccess } from "util/inbox-access";

export const inboxesQueryKey = (merchantId: number | null | undefined) => [
  "inboxes",
  merchantId ?? null,
];

/**
 * The merchant's Inboxes: the sidebar, the move destinations and the
 * translation of Release 1 Team links all read from here.
 */
export function useInboxes(merchantId?: number, staleTimeMs: number = 60_000) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const resolvedMerchantId = merchantId ?? merchant?.id;

  return useQuery<InboxDomain[], Error>({
    queryKey: inboxesQueryKey(resolvedMerchantId),
    enabled: Boolean(resolvedMerchantId),
    queryFn: async () => {
      if (!resolvedMerchantId) return [];
      return InboxService.getInboxes(auth0Context, resolvedMerchantId);
    },
    staleTime: staleTimeMs,
  });
}

/**
 * What a person can see may have changed (a grant, a member edit, a role):
 * the conversation lists and unread counts are read again. Both are the
 * server's answer for the person, so nothing is computed here. A request in
 * flight is replaced, since it may predate the change.
 */
export const refreshConversationAccess = (
  queryClient: QueryClient,
  merchantId: number | null
): void => {
  void queryClient.invalidateQueries({
    queryKey:
      merchantId === null
        ? ["conversations", "list"]
        : ["conversations", "list", merchantId],
  });
  void queryClient.invalidateQueries({ queryKey: unreadCountsQueryKey() });
};

// What the role gives on every open Inbox, merchant by merchant.
const roleAccessSignature = (agent: AgentDomain | undefined): string =>
  JSON.stringify(
    (agent?.merchants ?? []).map((merchant) => [
      merchant.id,
      merchant.isActive,
      roleInboxAccess(merchant.id, agent),
    ])
  );

/**
 * After `agent_updated` for the signed-in agent: their Inbox access
 * (`my_access` on every cached Inbox list) and their counts are read again.
 * The conversation lists are read again too when the role's conversation
 * access changed, since the event says nothing about what changed.
 */
export const refreshAfterCurrentAgentUpdate = (
  queryClient: QueryClient,
  previous: AgentDomain | undefined,
  next: AgentDomain
): void => {
  void queryClient.invalidateQueries({ queryKey: ["inboxes"] });

  if (roleAccessSignature(previous) !== roleAccessSignature(next)) {
    refreshConversationAccess(queryClient, null);
    return;
  }

  void queryClient.invalidateQueries({ queryKey: unreadCountsQueryKey() });
};

/**
 * Marks every Inbox list stale (navigation and both settings lists) whenever
 * an Inbox changes. Mounted for every private page because these events
 * arrive wherever the user is: a list left cached while
 * Settings was open would otherwise come back with the old names. An inactive
 * list is only marked stale and refetches when next used. A reconnect
 * refetches too, since events sent while disconnected are lost.
 */
export function useInboxesRealtimeUpdates() {
  const queryClient = useQueryClient();
  const { data: merchant } = useMerchant();
  const { socket, addEventHandler, removeEventHandler } = useWebSocket();

  useEffect(() => {
    const handleInboxChanged = (args: InboxChangedMessage) => {
      void queryClient.invalidateQueries({
        queryKey: conversationTemplatesMerchantQueryKey(args.merchant_id),
      });
      void queryClient.invalidateQueries({
        queryKey: inboxesQueryKey(args.merchant_id),
      });
    };
    // A member or grant edit publishes `inbox_updated` to the merchant room,
    // so it reaches the session of the person whose access changed, not
    // only the editor's. It carries no reason: a rename refreshes the same.
    const handleInboxUpdated = (args: InboxChangedMessage) => {
      handleInboxChanged(args);
      refreshConversationAccess(queryClient, args.merchant_id);
    };

    addEventHandler("inbox_created", handleInboxChanged);
    addEventHandler("inbox_updated", handleInboxUpdated);
    addEventHandler("inbox_archived", handleInboxChanged);

    return () => {
      removeEventHandler("inbox_created", handleInboxChanged);
      removeEventHandler("inbox_updated", handleInboxUpdated);
      removeEventHandler("inbox_archived", handleInboxChanged);
    };
  }, [queryClient, addEventHandler, removeEventHandler]);

  useEffect(() => {
    if (!socket || !merchant?.id) return;
    const refresh = () => {
      void queryClient.invalidateQueries({
        queryKey: conversationTemplatesMerchantQueryKey(merchant.id),
      });
      void queryClient.invalidateQueries(
        { queryKey: inboxesQueryKey(merchant.id) },
        { cancelRefetch: false }
      );
    };
    socket.on("connect", refresh);
    return () => {
      socket.off("connect", refresh);
    };
  }, [socket, queryClient, merchant?.id]);
}
